import { useMemo } from 'react';

import { useSessions } from '@/hooks/useSwr';

import { METRICS_COLUMNS } from '../../constants';

export const useSessionExtras = (
  sessions?: ReturnType<typeof useSessions>,
  firstSessionId?: string
) => {
  const sessionsData = sessions?.data?.rows;
  const isLoading = sessions?.isLoading;
  const aggregateFeedbackKeys = useMemo(() => {
    return !isLoading
      ? sessionsData?.reduce((acc, session) => {
          const feedbackStats = session.feedback_stats;
          if (feedbackStats) {
            Object.keys(feedbackStats).forEach((key) => {
              if (!acc.includes(key)) {
                acc.push(key);
              }
            });
          }
          return acc;
        }, [] as string[]) ?? []
      : [];
  }, [sessionsData, isLoading]);

  const aggregateMetadataKeys = useMemo(() => {
    return !isLoading
      ? sessionsData?.reduce((acc, session) => {
          const metadata = session.extra?.metadata;
          Object.keys(metadata ?? {}).forEach((key) => {
            if (!acc.includes(key)) {
              acc.push(key);
            }
          });
          return acc;
        }, [] as string[]) ?? []
      : undefined;
  }, [sessionsData, isLoading]);

  const aggregateSessionLevelFeedbackKeys = useMemo(
    () =>
      !isLoading
        ? sessionsData?.reduce((acc, session) => {
            const feedbackStats = session.session_feedback_stats;
            if (feedbackStats) {
              Object.keys(feedbackStats).forEach((key) => {
                if (!acc.includes(key)) {
                  acc.push(key);
                }
              });
            }
            return acc;
          }, [] as string[]) ?? []
        : null,
    [sessionsData, isLoading]
  );

  const feedbackRunFacets = useMemo(() => {
    return (
      sessionsData?.find((i) => i.id === firstSessionId)?.run_facets ?? []
    ).filter((facet) => facet.key === 'feedback_key');
  }, [sessionsData, firstSessionId]);

  const metricsRunFacets = useMemo(() => {
    return (
      sessionsData?.find((i) => i.id === firstSessionId)?.run_facets ?? []
    ).filter((facet) =>
      Object.values(METRICS_COLUMNS)
        .map((metric) =>
          'attribute' in metric ? metric.attribute : metric.name
        )
        .includes(facet.key)
    );
  }, [sessionsData, firstSessionId]);

  return {
    aggregateFeedbackKeys,
    aggregateMetadataKeys,
    aggregateSessionLevelFeedbackKeys,
    feedbackRunFacets,
    metricsRunFacets,
  };
};
